import React, { useState } from 'react';
import { Button, Fab, TextField, IconButton } from '@material-ui/core';
import BugReportIcon from '@material-ui/icons/BugReport';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import PersonIcon from '@material-ui/icons/Person';
import EmailIcon from '@material-ui/icons/Email';
import WorkIcon from '@material-ui/icons/Work';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { IState } from '../../data/types';
import {
  isStudent,
  isTrainer,
  isHubManager,
  isSuperAdmin,
} from '../../data/appRoles';
import { colors } from '../../theme/tokens';

const CORAL = colors.brand;
const ORANGE = colors.accentOrange;
const SUPPORT_EMAIL = 'bashir@era92.com';

const useStyles = makeStyles(() => ({
  fab: {
    position: 'fixed' as any,
    bottom: 80,
    right: 28,
    background: `linear-gradient(135deg, ${CORAL} 0%, ${ORANGE} 100%)`,
    color: colors.onBrand,
    boxShadow: '0 4px 20px rgba(254,58,106,0.40)',
    zIndex: 1300,
    '&:hover': {
      background: `linear-gradient(135deg, #e02d5c 0%, #e67d3a 100%)`,
      boxShadow: '0 6px 28px rgba(254,58,106,0.50)',
    },
  },

  // ── Chat panel ──────────────────────────────────────────────────
  panel: {
    position: 'fixed' as any,
    bottom: 132,
    right: 28,
    width: 360,
    background: colors.bgSurface,
    borderRadius: 18,
    boxShadow: '0 12px 48px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)',
    zIndex: 1299,
    display: 'flex',
    flexDirection: 'column' as any,
    maxHeight: 580,
    overflow: 'hidden',
    transformOrigin: 'bottom right',
    transition:
      'transform 0.22s cubic-bezier(0.34,1.56,0.64,1), opacity 0.18s ease',
  },
  panelHidden: {
    transform: 'scale(0.7) translateY(20px)',
    opacity: 0,
    pointerEvents: 'none' as any,
  },
  panelVisible: {
    transform: 'scale(1) translateY(0)',
    opacity: 1,
  },

  // ── Panel header ─────────────────────────────────────────────────
  panelHeader: {
    background: `linear-gradient(120deg, ${CORAL} 0%, ${ORANGE} 100%)`,
    padding: '14px 16px 14px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  headerIconBox: {
    width: 34,
    height: 34,
    borderRadius: 10,
    background: 'rgba(255,255,255,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerText: { flex: 1 },
  headerTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: colors.onBrand,
    lineHeight: 1.2,
  },
  headerSub: { fontSize: 11, color: 'rgba(255,255,255,0.80)', marginTop: 2 },
  closeBtn: { color: 'rgba(255,255,255,0.85)', padding: 4 },

  // ── Scrollable body ───────────────────────────────────────────────
  body: {
    flex: 1,
    overflowY: 'auto' as any,
    padding: '16px 18px',
  },

  // ── User info card ────────────────────────────────────────────────
  userCard: {
    background: colors.tintOrange,
    border: '1px solid rgba(254,58,106,0.13)',
    borderRadius: 12,
    padding: '12px 14px',
    marginBottom: 16,
    display: 'flex',
    flexDirection: 'column' as any,
    gap: 8,
  },
  userRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
  },
  userIconBox: {
    width: 26,
    height: 26,
    borderRadius: 7,
    background: colors.brandTint,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  userLabel: {
    fontSize: 11,
    color: colors.textFaint,
    width: 34,
    flexShrink: 0,
  },
  userValue: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.textPrimary,
    flex: 1,
    wordBreak: 'break-all' as any,
  },

  // ── Fields ────────────────────────────────────────────────────────
  field: {
    marginBottom: 12,
    '& .MuiOutlinedInput-root': { borderRadius: 10, fontSize: 13 },
    '& .MuiInputLabel-root': { fontSize: 13 },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.borderStrong,
    },
  },

  // ── Footer ───────────────────────────────────────────────────────
  footer: {
    padding: '10px 18px 14px',
    borderTop: `1px solid ${colors.border}`,
    flexShrink: 0,
  },
  sendBtn: {
    width: '100%',
    background: `linear-gradient(90deg, ${CORAL} 0%, ${ORANGE} 100%)`,
    color: colors.onBrand,
    borderRadius: 10,
    fontWeight: 700,
    textTransform: 'none' as any,
    fontSize: 13,
    padding: '9px 0',
    boxShadow: 'none',
    '&:hover': { opacity: 0.9, boxShadow: 'none' },
    '&:disabled': {
      background: colors.bgRaised,
      color: colors.textFaint,
      boxShadow: 'none',
    },
  },
}));

const BugReportFab: React.FC = () => {
  const classes = useStyles();
  const user = useSelector((state: IState) => state.core.user);

  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('Bug Report');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [sent, setSent] = useState(false);

  const toggle = () => setOpen((v) => !v);

  const handleClose = () => {
    setOpen(false);
    setSent(false);
    setSubject('Bug Report');
    setDescription('');
    setSteps('');
  };

  const handleSend = () => {
    if (!description.trim()) return;

    const lines = [
      `Name:    ${user?.fullName || 'N/A'}`,
      `Email:   ${user?.email || 'N/A'}`,
      `Role:    ${roleLabel}`,
      `User ID: ${user?.contactId || user?.id || 'N/A'}`,
      '',
      '──────────────────────────────',
      'DESCRIPTION',
      '──────────────────────────────',
      description.trim(),
    ];

    if (steps.trim()) {
      lines.push('', '──────────────────────────────');
      lines.push('STEPS TO REPRODUCE');
      lines.push('──────────────────────────────');
      lines.push(steps.trim());
    }

    const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(lines.join('\n'))}`;
    window.open(mailto, '_blank');
    setSent(true);
  };

  const roleLabel = (() => {
    if (!user) return '—';
    if (isSuperAdmin(user)) return 'Super Admin';
    if (isHubManager(user)) return 'Hub Manager';
    if (isTrainer(user)) return 'Trainer';
    if (isStudent(user)) return 'Student';
    const raw = user.roles?.filter(Boolean) ?? [];
    if (raw.length > 0)
      return raw
        .map((r) =>
          r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        )
        .join(', ');
    return 'User';
  })();

  return (
    <>
      {/* Chat-style panel */}
      <div
        className={`${classes.panel} ${
          open ? classes.panelVisible : classes.panelHidden
        }`}
      >
        {/* Header */}
        <div className={classes.panelHeader}>
          <div className={classes.headerIconBox}>
            <BugReportIcon style={{ fontSize: 18, color: colors.onBrand }} />
          </div>
          <div className={classes.headerText}>
            <div className={classes.headerTitle}>Report a Bug</div>
            <div className={classes.headerSub}>
              We'll look into it right away
            </div>
          </div>
          <IconButton
            size="small"
            className={classes.closeBtn}
            onClick={handleClose}
          >
            <CloseIcon style={{ fontSize: 18 }} />
          </IconButton>
        </div>

        {/* Scrollable body */}
        <div className={classes.body}>
          {/* User info */}
          <div className={classes.userCard}>
            <div className={classes.userRow}>
              <div className={classes.userIconBox}>
                <PersonIcon style={{ fontSize: 13, color: CORAL }} />
              </div>
              <span className={classes.userLabel}>Name</span>
              <span className={classes.userValue}>{user?.fullName || '—'}</span>
            </div>
            <div className={classes.userRow}>
              <div className={classes.userIconBox}>
                <EmailIcon style={{ fontSize: 13, color: CORAL }} />
              </div>
              <span className={classes.userLabel}>Email</span>
              <span className={classes.userValue}>{user?.email || '—'}</span>
            </div>
            <div className={classes.userRow}>
              <div className={classes.userIconBox}>
                <WorkIcon style={{ fontSize: 13, color: CORAL }} />
              </div>
              <span className={classes.userLabel}>Role</span>
              <span className={classes.userValue}>{roleLabel}</span>
            </div>
          </div>

          <TextField
            label="Subject"
            fullWidth
            variant="outlined"
            size="small"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className={classes.field}
          />

          <TextField
            label="Describe the bug *"
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What happened? What did you expect?"
            className={classes.field}
          />

          <TextField
            label="Steps to reproduce (optional)"
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={2}
            value={steps}
            onChange={(e) => setSteps(e.target.value)}
            placeholder="1. Go to…  2. Click…  3. See error"
            className={classes.field}
            style={{ marginBottom: 0 }}
          />
        </div>

        {/* Footer send button */}
        <div className={classes.footer}>
          {sent ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: colors.accentGreen,
                  marginBottom: 4,
                }}
              >
                ✓ Email client opened
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: colors.textMuted,
                  marginBottom: 10,
                }}
              >
                Complete the send in your email app
              </div>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClose}
                style={{ borderRadius: 8, textTransform: 'none', fontSize: 12 }}
              >
                Close
              </Button>
            </div>
          ) : (
            <Button
              variant="contained"
              disableElevation
              disabled={!description.trim()}
              className={classes.sendBtn}
              startIcon={<SendIcon style={{ fontSize: 15 }} />}
              onClick={handleSend}
            >
              Send Report
            </Button>
          )}
        </div>
      </div>

      {/* FAB toggle */}
      <Fab
        size="medium"
        className={classes.fab}
        onClick={toggle}
        title="Report a Bug"
      >
        {open ? (
          <CloseIcon style={{ fontSize: 20 }} />
        ) : (
          <BugReportIcon style={{ fontSize: 21 }} />
        )}
      </Fab>
    </>
  );
};

export default BugReportFab;
